import type { LineCoverageState } from '@his/shared';
import type { BadgeVariant } from '@/components/ui/badge';

export interface CoverageBadgeModel {
  variant: BadgeVariant;
  key: 'nx_covered' | 'nx_self_pay' | 'nx_awaiting_authorisation';
}

export function coverageBadge(state: LineCoverageState): CoverageBadgeModel {
  if (state === 'covered') return { variant: 'success', key: 'nx_covered' };
  if (state === 'pending_auth') return { variant: 'warning', key: 'nx_awaiting_authorisation' };
  return { variant: 'muted', key: 'nx_self_pay' };
}

interface CoverageItem {
  id: string;
  coverageState?: LineCoverageState;
  lockedByApproval?: boolean;
}

export function isApprovalLocked(item: Pick<CoverageItem, 'lockedByApproval'>): boolean {
  return item.lockedByApproval === true;
}

export function lockedItemIds(items: readonly CoverageItem[]): Set<string> {
  return new Set(items.filter(isApprovalLocked).map((item) => item.id));
}

export function hasApprovedLine(items: readonly Pick<CoverageItem, 'lockedByApproval'>[]): boolean {
  return items.some(isApprovalLocked);
}

export function followUpCandidates<T extends CoverageItem>(items: readonly T[], isUnsent: (item: T) => boolean): T[] {
  if (!hasApprovedLine(items)) return [];
  return items.filter((item) => item.coverageState === 'self_pay' && !isApprovalLocked(item) && isUnsent(item));
}

export type ExtraChoice = 'self_pay' | 'follow_up';

export function choiceOf(choices: Readonly<Record<string, ExtraChoice | undefined>>, itemId: string): ExtraChoice {
  return choices[itemId] ?? 'self_pay';
}

export function followUpIds(candidates: readonly CoverageItem[], choices: Readonly<Record<string, ExtraChoice | undefined>>): string[] {
  return candidates.filter((item) => choiceOf(choices, item.id) === 'follow_up').map((item) => item.id);
}
